import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ErrorBar,
  ResponsiveContainer,
} from 'recharts';
import type { ServiceLatencyRow } from '../../types/dashboard';

interface Props {
  data: ServiceLatencyRow[] | null;
}

export default function ServiceLatencyChart({ data }: Props) {
  if (!data || data.length === 0) {
    return <Placeholder text="No latency data available" />;
  }

  const chartData = data.map((r) => ({
    name: r.service,
    avg: r.avg_latency,
    // ErrorBar needs the delta from the center value
    errorLow: Math.max(r.avg_latency - r.p50, 0),
    errorHigh: Math.max(r.p95 - r.avg_latency, 0),
    p50: r.p50,
    p95: r.p95,
  }));

  return (
    <div style={sectionStyle}>
      <h3 style={headingStyle}>Service Latencies (Last 7 Days)</h3>
      <p style={descStyle}>Bar = avg, whiskers = p50–p95</p>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={false}
            axisLine={false}
            label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#9ca3af' } }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload[0]) return null;
              const d = payload[0].payload;
              return (
                <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>{d.name}</p>
                  <p style={{ margin: '4px 0 0', color: '#6b7280' }}>Avg: {Math.round(d.avg)} ms</p>
                  <p style={{ margin: '2px 0 0', color: '#6b7280' }}>p50: {Math.round(d.p50)} ms</p>
                  <p style={{ margin: '2px 0 0', color: '#6b7280' }}>p95: {Math.round(d.p95)} ms</p>
                </div>
              );
            }}
          />
          <Bar dataKey="avg" fill="#6366f1" radius={[4, 4, 0, 0]} name="avg">
            <ErrorBar
              dataKey="errorHigh"
              direction="y"
              width={8}
              strokeWidth={2}
              stroke="#374151"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <div style={{ ...sectionStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160, color: '#9ca3af' }}>
      {text}
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
};

const headingStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
  margin: '0 0 4px 0',
};

const descStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  margin: '0 0 16px 0',
};
